import { Icon } from '@hai/ui-react';
import React, { useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { NotificationVariant } from 'src/js/notification/notification';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';

interface CopyToClipboardProps {
  content: string;
  color?: string;
  className?: string;
  autoHide?: boolean;
}

const focusNextElementInTabOrder = (fromElement: HTMLElement | null) => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  const focusableElements = Array.from(document.querySelectorAll<HTMLElement>(focusableSelector)).filter((el) => {
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden' && !el.hasAttribute('aria-hidden');
  });

  if (!focusableElements.length) {
    document.body?.focus();
    return;
  }

  const currentIndex = fromElement ? focusableElements.indexOf(fromElement) : -1;
  const nextElement = focusableElements[currentIndex + 1] ?? focusableElements[0];
  nextElement.focus();
};

export const doCopyToClipboard = async (
  content: any,
  copySuccessMessage: string,
  copyFailureMessage: string,
  t: TFunction,
) => {
  if (!content) {
    return;
  }

  const originElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  try {
    if (window?.isSecureContext && navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(content);
      createAndDispatchNotification(copySuccessMessage, NotificationVariant.SUCCESS, t);
      setTimeout(() => {
        focusNextElementInTabOrder(originElement);
      }, 200);
      return;
    }

    const textArea = document.createElement('textarea');
    try {
      textArea.value = content;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand('copy');

      if (!copied) {
        throw new Error('Copy command failed');
      }
    } finally {
      if (textArea.parentNode) {
        textArea.parentNode.removeChild(textArea);
      }
    }

    createAndDispatchNotification(copySuccessMessage, NotificationVariant.SUCCESS, t);
    setTimeout(() => {
      focusNextElementInTabOrder(originElement);
    }, 200);
  } catch {
    createAndDispatchNotification(copyFailureMessage, NotificationVariant.ERROR, t);
  }
};
export const CopyToClipboard: React.FunctionComponent<CopyToClipboardProps> = ({
  content,
  color = 'haiui-gray-06',
  className,
  autoHide = false,
}) => {
  const { t } = useTranslation();
  const copySuccessMessage = t('notifications.clipboard.copySuccess');
  const copyFailureMessage = t('notifications.clipboard.copyFailure');
  const combinedClassName = ['mk-copy-clipboard', className, autoHide ? 'mk-auto-hide' : undefined]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    // copy-paste-area
  }, []);

  return (
    <div
      className={combinedClassName}
      role="button"
      tabIndex={0}
      onClick={() => doCopyToClipboard(content, copySuccessMessage, copyFailureMessage, t)}
      onKeyUp={(e) => e.key === 'Enter' && doCopyToClipboard(content, copySuccessMessage, copyFailureMessage, t)}
    >
      <Icon iconname="Copy" size="sm3" color={color} />
    </div>
  );
};
